import {
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Popper from "@mui/material/Popper";
import type { Instance } from "@popperjs/core";
import { KTSVG } from "../../../helpers/KTSVG";
import { tableControlBaseClasses } from "./tableControlStyles";

interface Props {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  withoutSearchIcon?: boolean;
  withoutSearchClear?: boolean;
}

// A portal changes DOM tab order; continue from the original table control instead.
function adjacentControl(
  anchor: HTMLElement,
  popup: HTMLElement | null,
  backwards: boolean,
) {
  const controls = Array.from(
    document.querySelectorAll<HTMLElement>(
      "a[href], button, input, select, textarea, [tabindex]",
    ),
  )
    .filter(
      (element) =>
        element === anchor ||
        (element.tabIndex >= 0 &&
          !element.matches(":disabled") &&
          !element.closest("[inert], [aria-hidden='true']") &&
          !popup?.contains(element) &&
          element.getClientRects().length > 0 &&
          getComputedStyle(element).visibility !== "hidden"),
    )
    .sort((a, b) => {
      const aIndex = a.tabIndex > 0 ? a.tabIndex : Infinity;
      const bIndex = b.tabIndex > 0 ? b.tabIndex : Infinity;
      return aIndex === bIndex ? 0 : aIndex - bIndex;
    });
  return controls[controls.indexOf(anchor) + (backwards ? -1 : 1)];
}

export default function ExpandableTableSearch({
  value,
  onChange,
  label,
  placeholder,
  withoutSearchIcon,
  withoutSearchClear,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [width, setWidth] = useState(320);
  const anchorRef = useRef<HTMLDivElement>(null);
  const compactRef = useRef<HTMLInputElement>(null);
  const compactClearRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const clearRef = useRef<HTMLButtonElement>(null);
  const popperRef = useRef<Instance | null>(null);
  const openingFrame = useRef(0);
  const pointerDown = useRef(false);
  const skipFocus = useRef(false);
  const restoreFocus = useRef(false);
  const composing = useRef(false);
  const selection = useRef({ start: 0, end: 0 });
  const hasClear = value.length > 0 && !withoutSearchClear;

  const focusCompact = useCallback(() => {
    const input = compactRef.current;
    if (!input) return;
    if (document.activeElement !== input) skipFocus.current = true;
    input.focus({ preventScroll: true });
    input.setSelectionRange(selection.current.start, selection.current.end);
  }, []);

  useLayoutEffect(() => {
    if (!expanded && restoreFocus.current) {
      restoreFocus.current = false;
      focusCompact();
    }
  }, [expanded, focusCompact]);

  const open = () => {
    cancelAnimationFrame(openingFrame.current);
    const input = compactRef.current;
    if (expanded || composing.current || !input) return;
    selection.current = {
      start: input.selectionStart ?? value.length,
      end: input.selectionEnd ?? value.length,
    };
    setWidth(
      Math.max(320, anchorRef.current?.getBoundingClientRect().width ?? 0),
    );
    setExpanded(true);
  };

  const onCompactFocus = () => {
    if (skipFocus.current) {
      skipFocus.current = false;
      return;
    }
    // Keep the original pointer target mounted until its click has completed.
    if (pointerDown.current) return;
    // Read the caret after the browser has finished positioning it on click/focus.
    openingFrame.current = requestAnimationFrame(() => {
      if (document.activeElement === compactRef.current) open();
    });
  };

  const mountInput = useCallback((input: HTMLInputElement | null) => {
    inputRef.current = input;
    if (input) {
      input.focus({ preventScroll: true });
      input.setSelectionRange(selection.current.start, selection.current.end);
    }
  }, []);

  const anchor = useMemo(() => {
    const element = anchorRef.current;
    if (!expanded || !element) return null;
    return {
      contextElement: element,
      getBoundingClientRect: () => {
        const rect = element.getBoundingClientRect();
        return new DOMRect(rect.x, rect.y, rect.width, 0);
      },
    };
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const element = anchorRef.current;
    if (!element) return;
    const scroller = element.closest<HTMLElement>("[data-table-scroll]");
    const reposition = () => {
      const rect = element.getBoundingClientRect();
      const boundary = scroller?.getBoundingClientRect();
      if (
        !element.isConnected ||
        rect.width === 0 ||
        rect.right <= Math.max(0, boundary?.left ?? 0) ||
        rect.left >= Math.min(window.innerWidth, boundary?.right ?? Infinity) ||
        rect.bottom <= 0 ||
        rect.top >= window.innerHeight
      ) {
        setExpanded(false);
        return;
      }
      setWidth(Math.max(320, rect.width));
      void popperRef.current?.update();
    };
    const observer = new ResizeObserver(reposition);
    observer.observe(element);
    if (scroller) observer.observe(scroller);
    document.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    window.visualViewport?.addEventListener("resize", reposition);
    return () => {
      observer.disconnect();
      document.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      window.visualViewport?.removeEventListener("resize", reposition);
    };
  }, [expanded]);

  useEffect(() => () => cancelAnimationFrame(openingFrame.current), []);

  const onPopupBlur = (event: FocusEvent) => {
    const next = event.relatedTarget as Node | null;
    if (
      next &&
      (popupRef.current?.contains(next) || anchorRef.current?.contains(next))
    )
      return;
    setExpanded(false);
  };

  const onKeyDown = (event: KeyboardEvent, fromClear = false) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      selection.current = {
        start: inputRef.current?.selectionStart ?? value.length,
        end: inputRef.current?.selectionEnd ?? value.length,
      };
      restoreFocus.current = true;
      setExpanded(false);
    } else if (event.key === "Tab") {
      if (
        (!event.shiftKey && !fromClear && hasClear) ||
        (event.shiftKey && fromClear)
      ) {
        event.preventDefault();
        (fromClear ? inputRef.current : clearRef.current)?.focus();
        return;
      }
      const original =
        !event.shiftKey && hasClear
          ? compactClearRef.current
          : compactRef.current;
      if (!original) return;
      const next = adjacentControl(original, popupRef.current, event.shiftKey);
      setExpanded(false);
      if (next) {
        event.preventDefault();
        next.focus();
      }
    }
  };

  const clear = (isExpanded: boolean) => {
    onChange("");
    selection.current = { start: 0, end: 0 };
    if (isExpanded) inputRef.current?.focus();
    else focusCompact();
  };

  const icons = (isExpanded: boolean) => (
    <>
      {!withoutSearchIcon && (
        <KTSVG
          path="/media/icons/duotune/general/gen021.svg"
          className="pointer-events-none absolute left-2"
          svgClassName="h-5 w-5"
        />
      )}
      {hasClear && (
        <button
          ref={isExpanded ? clearRef : compactClearRef}
          type="button"
          aria-label={`Sterge cautarea ${label}`}
          tabIndex={!isExpanded && expanded ? -1 : undefined}
          className="absolute right-1 inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent transition hover:bg-surface-hover"
          onMouseDown={(event) => event.preventDefault()}
          onKeyDown={isExpanded ? (event) => onKeyDown(event, true) : undefined}
          onClick={() => clear(isExpanded)}>
          <KTSVG
            path="/media/icons/duotune/general/gen040.svg"
            svgClassName="h-5 w-5"
          />
        </button>
      )}
    </>
  );
  const inputClass = `${tableControlBaseClasses} w-full min-w-0 text-left pr-9 ${
    withoutSearchIcon ? "pl-4" : "pl-9"
  }`;

  return (
    <ClickAwayListener
      mouseEvent={expanded ? "onClick" : false}
      touchEvent={expanded ? "onTouchEnd" : false}
      onClickAway={() => setExpanded(false)}>
      <div ref={anchorRef} className="relative h-[34px] w-full min-w-0">
        <div
          className="relative flex w-full items-center"
          style={{ visibility: expanded ? "hidden" : undefined }}>
          {icons(false)}
          <input
            ref={compactRef}
            type="text"
            data-kt-user-table-filter="search"
            aria-label={`Cauta ${label}`}
            aria-hidden={expanded || undefined}
            tabIndex={expanded ? -1 : undefined}
            className={inputClass}
            placeholder={placeholder}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onFocus={onCompactFocus}
            onPointerDown={() => {
              pointerDown.current = true;
            }}
            onPointerUp={() => {
              pointerDown.current = false;
            }}
            onPointerCancel={() => {
              pointerDown.current = false;
            }}
            onClick={() => {
              pointerDown.current = false;
              open();
            }}
            onBlur={() => {
              pointerDown.current = false;
              cancelAnimationFrame(openingFrame.current);
            }}
            onCompositionStart={() => {
              composing.current = true;
              cancelAnimationFrame(openingFrame.current);
            }}
            onCompositionEnd={() => {
              composing.current = false;
              onCompactFocus();
            }}
          />
        </div>
        <Popper
          open={expanded}
          anchorEl={anchor}
          popperRef={popperRef}
          placement="bottom-start"
          container={
            anchorRef.current?.closest(".MuiDialog-container") ?? undefined
          }
          modifiers={[
            { name: "preventOverflow", options: { padding: 12 } },
            { name: "flip", options: { padding: 12 } },
          ]}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
          <div
            ref={popupRef}
            data-table-search-expanded
            className="relative flex items-center rounded-control bg-white shadow-card"
            style={{ width, maxWidth: "calc(100vw - 24px)" }}
            onBlur={onPopupBlur}>
            {icons(true)}
            <input
              ref={mountInput}
              type="text"
              aria-label={`Cauta ${label}`}
              className={inputClass}
              placeholder={placeholder}
              value={value}
              onChange={(event) => onChange(event.target.value)}
              onKeyDown={(event) => onKeyDown(event)}
            />
          </div>
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
